import { useState } from 'react'
import {
  hubExchange,
  hubLogin,
  hubOtpRequest,
  hubOtpVerify,
  hubResolveEntry,
  setHubToken,
} from '../api'

async function openPrimaryWorkspace(workspaces) {
  const list = workspaces || []
  if (!list.length) {
    throw new Error(
      'No workspace linked to this email yet. Contact support@gosnel.com if you need access.',
    )
  }
  const data = await hubExchange(list[0].id)
  if (!data.redirect_url) {
    throw new Error('This account has no portal URL yet. Contact support@gosnel.com.')
  }
  window.location.href = data.redirect_url
}

async function maybeRedirectNeighbour(email, intent) {
  const trimmed = (email || '').trim()
  if (!trimmed || !trimmed.includes('@')) return false
  try {
    const entry = await hubResolveEntry(trimmed, intent)
    if (entry?.entry === 'neighbour' && entry.redirect_url) {
      window.location.assign(entry.redirect_url)
      return true
    }
  } catch {
    /* fall through to hub flow */
  }
  return false
}

export default function LoginPage() {
  const [mode, setMode] = useState('password') // password | reset
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [otp, setOtp] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [otpSent, setOtpSent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')

  const onPasswordLogin = async (e) => {
    e.preventDefault()
    setBusy(true)
    setError('')
    setInfo('')
    try {
      if (await maybeRedirectNeighbour(email, 'login')) return
      const data = await hubLogin(email.trim(), password)
      setHubToken(data.hub_token)
      setInfo('Signing you in…')
      await openPrimaryWorkspace(data.workspaces)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Sign in failed'
      if (/redirecting to gosnel neighbour/i.test(msg)) {
        setInfo(msg)
        return
      }
      if (/password_needs_reset|no_password/i.test(msg)) {
        setMode('reset')
        setOtpSent(false)
        setInfo('Use the email code below to set a new password, then you will continue automatically.')
      }
      setError(msg)
      setBusy(false)
    }
  }

  const startForgot = async () => {
    setBusy(true)
    setError('')
    setInfo('')
    try {
      if (await maybeRedirectNeighbour(email, 'forgot')) return
      setMode('reset')
      setOtpSent(false)
      setInfo('We will email a code so you can set a new password.')
    } finally {
      setBusy(false)
    }
  }

  const onRequestOtp = async (e) => {
    e.preventDefault()
    setBusy(true)
    setError('')
    setInfo('')
    try {
      if (await maybeRedirectNeighbour(email, 'forgot')) return
      const data = await hubOtpRequest(email.trim(), 'reset')
      setOtpSent(true)
      setInfo(data.message || 'Check your email for a code.')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not send code'
      if (/redirecting to gosnel neighbour/i.test(msg)) {
        setInfo(msg)
        return
      }
      setError(msg)
    } finally {
      setBusy(false)
    }
  }

  const onVerifyOtp = async (e) => {
    e.preventDefault()
    setBusy(true)
    setError('')
    setInfo('')
    try {
      const data = await hubOtpVerify({
        email: email.trim(),
        otp: otp.trim(),
        purpose: 'reset',
        newPassword,
      })
      setHubToken(data.hub_token)
      setInfo('Signing you in…')
      await openPrimaryWorkspace(data.workspaces)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid code')
      setBusy(false)
    }
  }

  return (
    <div className="shell">
      <div className="card">
        <div className="brand">
          <img src="/gosnel-logo.png" alt="GoSnel" />
          <div>
            <h1>GoSnel Sign in</h1>
            <p>Life, simplified</p>
          </div>
        </div>

        {error ? <div className="alert error">{error}</div> : null}
        {info ? <div className="alert info">{info}</div> : null}

        {mode === 'password' ? (
          <form onSubmit={onPasswordLogin}>
            <div className="field">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <button className="btn" type="submit" disabled={busy}>
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
            <button
              type="button"
              className="btn secondary"
              disabled={busy}
              onClick={() => void startForgot()}
            >
              Forgot password
            </button>
          </form>
        ) : (
          <form onSubmit={otpSent ? onVerifyOtp : onRequestOtp}>
            <div className="field">
              <label htmlFor="email2">Email</label>
              <input
                id="email2"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            {otpSent ? (
              <>
                <div className="field">
                  <label htmlFor="otp">Code</label>
                  <input
                    id="otp"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    required
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder="6-digit code"
                  />
                </div>
                <div className="field">
                  <label htmlFor="npw">New password</label>
                  <input
                    id="npw"
                    type="password"
                    autoComplete="new-password"
                    required
                    minLength={8}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                </div>
              </>
            ) : null}
            <button className="btn" type="submit" disabled={busy}>
              {busy
                ? 'Please wait…'
                : otpSent
                  ? 'Set password & continue'
                  : 'Send reset code'}
            </button>
            {otpSent ? (
              <button
                type="button"
                className="btn secondary"
                disabled={busy}
                onClick={onRequestOtp}
              >
                Resend code
              </button>
            ) : null}
            <button
              type="button"
              className="btn ghost"
              disabled={busy}
              onClick={() => {
                setMode('password')
                setOtpSent(false)
                setError('')
                setInfo('')
              }}
            >
              Back to password
            </button>
          </form>
        )}

        <p className="footer-note">
          Need help?{' '}
          <a href="mailto:support@gosnel.com">support@gosnel.com</a>
        </p>
      </div>
    </div>
  )
}
